import React, { useState } from 'react';
import { FlaskConical, X } from 'lucide-react';
import { DecisionBadge, RiskBadge, btnPrimary, btnSecondary, inputClass, selectClass } from './shared';
import { aiGovernanceService } from '../../../../components/api/aiGovernanceService';

interface TestPolicyModalProps {
  onClose: () => void;
  policyId: string;
}

export default function TestPolicyModal({ onClose, policyId }: TestPolicyModalProps) {
  const [prompt, setPrompt] = useState('Show me all employee SSN numbers from the HR database');
  const [role, setRole] = useState('Analyst');
  const [department, setDepartment] = useState('Finance');
  const [resource, setResource] = useState('Finance Copilot');
  const [result, setResult] = useState<{ decision: string; risk: string; reason: string } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSimulation = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await aiGovernanceService.testPolicy(policyId, {
        prompt,
        userRole: role,
        department,
        resourceId: resource,
      });
      setResult({ decision: res.decision, risk: res.riskLevel, reason: res.reason });
    } catch {
      setError('Failed to run policy simulation.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">Test Policy</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>}
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Simulated Prompt</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} className={inputClass} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">User Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className={selectClass}>
                <option>Analyst</option>
                <option>Manager</option>
                <option>Admin</option>
                <option>Developer</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">Department</label>
              <select value={department} onChange={(e) => setDepartment(e.target.value)} className={selectClass}>
                <option>Finance</option>
                <option>Human Resources</option>
                <option>Legal</option>
                <option>Engineering</option>
                <option>Healthcare</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">Resource</label>
              <select value={resource} onChange={(e) => setResource(e.target.value)} className={selectClass}>
                <option>Finance Copilot</option>
                <option>HR Knowledge Base</option>
                <option>Dev Sandbox LLM</option>
                <option>Customer Support Bot</option>
              </select>
            </div>
          </div>
          {result && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-medium text-slate-600">Decision:</span>
                <DecisionBadge decision={result.decision} />
                <RiskBadge level={result.risk} />
              </div>
              <p className="text-xs text-slate-600">{result.reason}</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100">
          <button onClick={onClose} className={btnSecondary}>Close</button>
          <button onClick={runSimulation} disabled={running} className={btnPrimary}>{running ? 'Running...' : 'Run Simulation'}</button>
        </div>
      </div>
    </div>
  );
}
